/**
 * The face/body-diagram mapping primitive (`BACKLOG.md` M9, `docs/research/specialty-module-
 * requirements.md`'s M7 body-diagram note, M9's own header: "the same face/body-diagram mapping
 * primitive derm needs for lesion location").
 *
 * **Built here, not under `dermatology/`.** `BACKLOG.md` M3's own entry is explicit that dermatology
 * "did not build" a body-mapping UI — M9 is the first module to need one at all, so this is a fresh
 * primitive, not an extraction from existing derm code. It lives at `shared/src/` top level (not
 * nested under an `aesthetics/` subdirectory) specifically so a future module — derm's own lesion
 * location, M7's ortho exam-maneuver diagram — can import it directly rather than depending on the
 * aesthetics module or re-deriving an equivalent region list, per `claude-kit/rules/invariants.md`'s
 * "favor the general, reusable solution once a pattern actually recurs."
 *
 * A site is a closed **region** (which named area of the face or body), an optional **side** (most
 * facial/body regions are bilateral; a handful are midline and carry no side), plus an optional
 * free-text `label` for a clinician's own qualifier ("just above the brow", "2cm lateral to X") — the
 * label is never itself validated or matched against, only the region/side pair is, matching every
 * other "closed enum plus free elaboration" split in this schema (`dermatology_destruction_
 * procedures.body_site`'s own free-text convention, e.g.).
 *
 * Storage is **open-but-checked**, matching `dermatology_patch_test_readings.reading`'s own
 * convention (`server/src/dermatology/routes.ts`'s header comment on the response/request schema
 * split): the database column is plain `text`, and `isBodyMapRegion`/`isBodyMapSide` are the single
 * place that validates against the closed set, at the application boundary, so a region added here
 * requires no migration.
 */

/**
 * Facial regions, grouped by the areas an injectable chart (Botox/filler) and a derm exam most
 * commonly reference. Not exhaustive of every possible facial landmark — extend deliberately, the
 * same discipline `authorization.ts`'s own header states for `ROLES`: a region added here without a
 * corresponding UI diagram hotspot is a silent gap, not a free addition.
 */
export const FACE_BODY_MAP_REGIONS = [
  'forehead',
  'glabella',
  'crows_feet',
  'brow',
  'temple',
  'cheek',
  'nasolabial_fold',
  'nose',
  'lips',
  'perioral',
  'marionette_lines',
  'chin',
  'jawline',
  'neck',
  'scalp',
  'ear',
] as const;

/** Non-facial body regions. Originally kept intentionally small for M9 (a face-forward specialty);
 * `shoulder`/`elbow`/`wrist`/`hip`/`knee`/`ankle` were added for `BACKLOG.md` M7 (orthopedics), whose
 * exam maneuvers (Lachman → knee, Neer/Hawkins-Kennedy → shoulder, Tinel's/Phalen's → wrist) and
 * condition templates (fracture, joint replacement, ACL, rotator cuff, carpal tunnel) are all named for
 * a specific joint the pre-existing regions had no entry for — extending this shared enum per this
 * module's own header ("a region added here without a corresponding UI diagram hotspot is a silent
 * gap, not a free addition") rather than forking a second, ortho-only region list. Each new region has
 * a matching `bodyMapGeometry.ts` entry, per that module's own `Record<BodyMapRegion, RegionGeometry>`
 * exhaustiveness discipline. */
export const NON_FACE_BODY_MAP_REGIONS = [
  'chest',
  'abdomen',
  'back',
  'shoulder',
  'upper_arm',
  'elbow',
  'forearm',
  'wrist',
  'hand',
  'buttock',
  'hip',
  'thigh',
  'knee',
  'lower_leg',
  'ankle',
  'foot',
  'other',
] as const;

export const BODY_MAP_REGIONS = [...FACE_BODY_MAP_REGIONS, ...NON_FACE_BODY_MAP_REGIONS] as const;
export type BodyMapRegion = (typeof BODY_MAP_REGIONS)[number];

/**
 * `midline` for a region with no left/right distinction (glabella, chin, nose, lips, perioral,
 * forehead, chest, abdomen, back, scalp) — a caller may still supply `left`/`right` for one of those
 * regions (nothing here refuses it structurally), but the UI diagram is expected to default to
 * `midline` and not offer a side selector for them. `null` is reserved for "not recorded" at the
 * request-schema layer, not a fourth value here.
 */
export const BODY_MAP_SIDES = ['left', 'right', 'midline'] as const;
export type BodyMapSide = (typeof BODY_MAP_SIDES)[number];

function includes(list: readonly string[], value: string): boolean {
  return list.includes(value);
}

export function isBodyMapRegion(value: string): value is BodyMapRegion {
  return includes(BODY_MAP_REGIONS, value);
}

export function isBodyMapSide(value: string): value is BodyMapSide {
  return includes(BODY_MAP_SIDES, value);
}

/** One mapped site: a region, an optional side, and a clinician's own free-text elaboration. */
export interface BodyMapSite {
  readonly region: BodyMapRegion;
  readonly side: BodyMapSide | null;
  readonly label: string | null;
}

/** A human-readable label for one mapped site, for a list/summary view — not the diagram itself,
 * which is a UI concern this module deliberately knows nothing about: `web/src/routes/
 * PatientAestheticsPage.tsx` renders region/side as plain selects today rather than a click-map
 * diagram, a UI investment flagged as out of scope for this pass (see this task's own report). */
export function describeBodyMapSite(site: BodyMapSite): string {
  const region = site.region.replaceAll('_', ' ');
  const sided = site.side === null || site.side === 'midline' ? region : `${site.side} ${region}`;
  return site.label === null ? sided : `${sided} (${site.label})`;
}
